import React, { Profiler, useEffect, useState } from "react";
import {
    FaSignOutAlt,
    FaQuestionCircle,
    FaHistory,
    FaClipboardCheck,
    FaGraduationCap,
    FaStar,
    FaFire,
    FaTrophy,
    FaBell,
    FaChartLine,
    FaChartBar,
    FaUsers,
    FaLaptopCode,
    FaBookOpen,
    FaBrain,
    FaCompass,
    FaRobot,
    FaShoppingBag,
    FaVideo,
    FaMedal,
    FaPenFancy
} from "react-icons/fa";
import {
    Atom,
    TestTube,
    Leaf,
    Calculator,
    Sprout,
    TrendingUp,
    Cross,
    PenTool,
    Book,
    Microscope,
    Monitor,
    Globe,
    Users as UsersIcon,
    Heart,
    Briefcase,
    Palette,
    Home,
    Shield,
    BookOpen,
    FileText,
    BarChart3,
    Building2,
    Coffee,
    Activity,
    Target,
    Gamepad2,
    Zap,
    Plus,
    TreePine,
    ChevronDown,
    ChevronUp,
    Settings,
    Brain,
    Clock,
    Calendar,
    Play,
    FunctionSquare,
    User,
    User2,
    Star,
    Wrench
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import WOW from "wowjs";
import "animate.css";
import { API_BASE_URL } from "../../config/apiConfig";
import CountdownTimer from "../../components/timerComponent";
import SubscriptionStatus from "../../components/SubscriptionStatus";
import toast, { Toaster } from "react-hot-toast";

const Dashboard = () => {
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const [showAllSubjects, setShowAllSubjects] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [activeCard, setActiveCard] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [studyStats, setStudyStats] = useState({
        total_hours: 0,
        streak: 0,
    });
    const [recentActivity, setRecentActivity] = useState([]);

    const token = localStorage.getItem("token");
    const [subscribed, setSubscribed] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) {
            setSubscribed(true); // assume subscribed or hide modal until login
            return;
        }

        const fetchSubscriptionStatus = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/sub/single`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error("Failed to fetch subscription");
                const data = await res.json();
                const isSubscribed = data?.subscriptions?.[0]?.status === "Active";
                setSubscribed(isSubscribed);
            } catch (err) {
                console.error(err);
                setSubscribed(false);
            }
        };

        fetchSubscriptionStatus();
    }, [token]); // 👈 runs again whenever token changes


    const [examStats, setExamStats] = useState({
        completed_tests: 0,
        average_score: 0,
        tests_trend: "+0",
        score_trend: "+0%",
    });


    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;

                const res = await fetch(`${API_BASE_URL}/api/notifications`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (!res.ok) throw new Error("Failed to fetch notifications");

                const data = await res.json();
                const count = data.data.filter(n => n.unread).length;
                setUnreadCount(count);
            } catch (err) {
                console.error("Error fetching notifications:", err);
            }
        };

        fetchUnreadCount();
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }, []); // runs only on mount


    useEffect(() => {
        const fetchExams = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/tests/analysis`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch exam data");
                }

                const exams = await response.json();
                console.log(exams.data);

                const cutoffDate = new Date(); // set your desired cutoff
                const stats = calculateStudyStats(exams.data, cutoffDate);
                const recent = getRecentActivity(exams.data, 4);
                setRecentActivity(recent);


                // Save to the dedicated exam stats state
                setExamStats(stats);

            } catch (error) {
                console.error("Error fetching exam data:", error.message);
            }
        };

        fetchExams();
    }, []);

    const timeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffSeconds = Math.floor((now - date) / 1000);

        if (diffSeconds < 60) return `${diffSeconds} seconds ago`;
        if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)} minutes ago`;
        if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)} hours ago`;
        return `${Math.floor(diffSeconds / 86400)} days ago`;
    };



    const calculateStudyStats = (rawExamData, cutoffDate) => {
        // Ignore orphaned answers (no associated test) so a single bad row can't
        // crash the dashboard. Covers regular + JAMB/WAEC (promo) answers.
        const examData = (rawExamData || []).filter(a => a && a.test);
        // Helper: group answers by test_id and calculate test scores
        const getTestScores = (data) => {
            const testsMap = {};
            data.forEach(answer => {
                const testId = answer.test.id;
                if (!testsMap[testId]) {
                    testsMap[testId] = { total: 0, correct: 0 };
                }
                testsMap[testId].total += 1;
                testsMap[testId].correct += Number(answer.is_correct || 0);
            });
            return Object.entries(testsMap).map(([testId, test]) =>
                Math.round((test.correct / test.total) * 100)
            );
        };

        // All unique tests (for completedTests)
        const allTestIds = new Set(examData.map(answer => answer.test.id));
        const completedTests = allTestIds.size;

        // Split exams into previous vs current based on cutoffDate for trends
        const previousExams = examData.filter(answer => new Date(answer.test.createdAt) < cutoffDate);
        const currentExams = examData.filter(answer => new Date(answer.test.createdAt) >= cutoffDate);

        const currentScores = getTestScores(currentExams);
        const previousScores = getTestScores(previousExams);

        const averageScore = currentScores.length > 0
            ? Math.round(currentScores.reduce((a, b) => a + b, 0) / currentScores.length)
            : 0;

        const prevAverageScore = previousScores.length > 0
            ? Math.round(previousScores.reduce((a, b) => a + b, 0) / previousScores.length)
            : 0;

        const prevCompletedTests = new Set(previousExams.map(answer => answer.test.id)).size;
        const testsTrend = completedTests - prevCompletedTests;
        const scoreTrend = averageScore - prevAverageScore;

        return {
            completed_tests: completedTests,
            average_score: averageScore,
            tests_trend: testsTrend >= 0 ? `+${testsTrend}` : `${testsTrend}`,
            score_trend: scoreTrend >= 0 ? `+${scoreTrend}%` : `${scoreTrend}%`,
        };
    };



    const quickActions = [
        // Core Learning & Practice
        {
            id: 1,
            title: "Study Mode",
            description: "Learn concepts at your own pace",
            icon: <FaBookOpen color="#2148C0" />,
            path: "/take/study",
            color: "from-blue-500 to-sky-600", // deep to bright blue
        },
        {
            id: 2,
            title: "Exam Mode",
            description: "Simulate real exam conditions",
            icon: <FaLaptopCode color="#2148C0" />,
            path: "/choose-exam",
            color: "from-red-500 to-orange-600", // bold red-orange
        },

        // Community First
        {
            id: 3,
            title: "Groups",
            description: "Create and join groups, manage exams",
            icon: <FaUsers color="#2148C0" />,
            path: "/groups",
            color: "from-green-500 to-emerald-600", // fresh green tones
            requiresSubscription: true,
        },
        {
            id: 4,
            title: "Leaderboard",
            description: "See top performers and rankings",
            icon: <FaTrophy color="#2148C0" />,
            path: "/leaderboard",
            color: "from-yellow-400 to-amber-600", // gold gradient
            requiresSubscription: true,
        },
        {
            id: 5,
            title: "Exam Performance",
            description: "View detailed performance per exam",
            icon: <FaChartBar color="#2148C0" />,
            path: "/exam-performance",
            color: "from-purple-500 to-fuchsia-600", // vivid purple-pink
        },

        // Performance & Tracking
        {
            id: 6,
            title: "Performance Insights",
            description: "Coming soon",
            icon: <FaChartLine color="#2148C0" />,
            path: null,
            color: "from-cyan-500 to-teal-600", // fresh cyan tone
            comingSoon: true,
        },
        {
            id: 7,
            title: "Exam History",
            description: "Review your past exam attempts",
            icon: <FaHistory color="#2148C0" />,
            path: "/exam-history",
            color: "from-indigo-500 to-violet-600", // royal indigo gradient
        },

        // Support tool (last)
        {
            id: 8,
            title: "Search Questions",
            description: "Find questions and explanations",
            icon: <FaQuestionCircle color="#2148C0" />,
            path: "/search-questions",
            color: "from-rose-500 to-pink-600", // cheerful rose-pink
            requiresSubscription: true,
        },

        // AI-powered lesson & performance features
        {
            id: 9,
            title: "My Lessons",
            description: "Lessons and AI practice from your teacher",
            icon: <FaBookOpen color="#2148C0" />,
            path: "/lessons",
            color: "from-sky-500 to-blue-600",
        },
        {
            id: 10,
            title: "Study Plan",
            description: "What to study next, based on your results",
            icon: <FaBrain color="#2148C0" />,
            path: "/study-plan",
            color: "from-teal-500 to-cyan-600",
        },
        {
            id: 11,
            title: "Readiness",
            description: "Your WAEC, JAMB and per-subject readiness score",
            icon: <FaGraduationCap color="#2148C0" />,
            path: "/readiness",
            color: "from-emerald-500 to-green-600",
        },
        {
            id: 12,
            title: "Predictions",
            description: "Projected grades based on your recent trend",
            icon: <FaCompass color="#2148C0" />,
            path: "/predictions",
            color: "from-violet-500 to-purple-600",
        },

        // Phase 2: assistant, mock exams, marketplace, live, achievements, guidance, essay
        {
            id: 13,
            title: "AI Assistant",
            description: "Ask anything, get a simple explanation",
            icon: <FaRobot color="#2148C0" />,
            path: "/assistant",
            color: "from-indigo-500 to-blue-600",
        },
        {
            id: 14,
            title: "Mock Exam",
            description: "Generate a fresh practice exam on demand",
            icon: <FaLaptopCode color="#2148C0" />,
            path: "/mock-exam",
            color: "from-red-500 to-rose-600",
        },
        {
            id: 15,
            title: "Marketplace",
            description: "Premium content from your teachers",
            icon: <FaShoppingBag color="#2148C0" />,
            path: "/marketplace",
            color: "from-amber-500 to-orange-600",
        },
        {
            id: 16,
            title: "Live Sessions",
            description: "Join live classes and watch recordings",
            icon: <FaVideo color="#2148C0" />,
            path: "/live",
            color: "from-pink-500 to-rose-600",
        },
        {
            id: 17,
            title: "Achievements",
            description: "Streaks, badges and milestones",
            icon: <FaMedal color="#2148C0" />,
            path: "/achievements",
            color: "from-yellow-500 to-amber-600",
        },
        {
            id: 18,
            title: "Guidance",
            description: "Career paths and university guidance",
            icon: <FaCompass color="#2148C0" />,
            path: "/guidance",
            color: "from-teal-500 to-emerald-600",
        },
        {
            id: 19,
            title: "Essay Practice",
            description: "Instant AI feedback on written answers",
            icon: <FaPenFancy color="#2148C0" />,
            path: "/essay",
            color: "from-slate-500 to-gray-600",
        },
    ];




    const subjectIcons = {
        "Physics": Atom,
        "Chemistry": TestTube,
        "Biology": Leaf,
        "Further Mathematics": FunctionSquare,
        "Mathematics": Calculator,
        "Agricultural Science": Sprout,
        "Commerce": TrendingUp,
        "CRS": Cross,
        "Christian Religious Studies (CRS)": Cross,
        "English Language": PenTool,
        "English Studies": Book,
        "Basic Science": Microscope,
        "Basic Technology": Monitor,
        "Social Studies": Globe,
        "Civic Education": UsersIcon,
        "Computer Science": Monitor,
        "Physical and Health Education": Heart,
        "Physical and Health Education (PHE)": Heart,
        "Business Studies": Briefcase,
        "Creative Arts": Palette,
        "Home Economics": Home,
        "Security Education": Shield,
        "Literature": BookOpen,
        "Geography": Globe,
        "History": FileText,
        "Economics": BarChart3,
        "Government": Building2,
        "French": Coffee,
        "Arabic": Book,
        "Hausa": Book,
        "Igbo": Book,
        "Yoruba": Book,
        "Fine Arts": Palette,
        "Music": Activity,
        "Technical Drawing": Target,
        "Auto Mechanics": Gamepad2,
        "Electronics": Zap,
        "Building Construction": Building2,
        "Metal Work": Plus,
        "Wood Work": TreePine,
        "Food and Nutrition": Home,
        "Clothing and Textiles": Home
    };



    useEffect(() => {
        new WOW.WOW({
            live: true,
            boxClass: "wow",
            animateClass: "animate__animated",
            offset: 0,
            mobile: true,
            duration: 2,
        }).init();
    }, []);

    // Fetch user profile
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/users/me`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`
                    },
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch user profile");
                }

                const userData = await response.json();
                const filteredData = {
                    firstName: userData.firstName || "",
                    lastName: userData.lastName || "",
                    email: userData.email || "",
                    phoneNumber: userData.phoneNumber || "",
                    gender: userData.gender || "",
                    dob: userData.dob || "",
                    country: userData.country || "",
                    state: userData.state || "",
                    accountType: userData.accountType || "",
                    yearGroup: userData.yearGroup || "",
                    school: userData.school || "",
                    subjects: userData.subjects || "[]",
                    guardianEmail: userData.guardianEmail || "",
                    isVerified: userData.isVerified || false,
                    subscribed: userData.subscribed || false
                };

                setUserData(filteredData);

            } catch (error) {
                console.error("Error fetching user profile:", error.message);
            }
        };

        fetchProfile();
    }, []);

    // Parse subjects from string
    const parseSubjects = (subjectsString) => {
        try {
            return JSON.parse(subjectsString || "[]");
        } catch {
            return [];
        }
    };

    const userSubjects = userData ? parseSubjects(userData.subjects) : [];
    const displayedSubjects = showAllSubjects ? userSubjects : userSubjects.slice(0, 6);

    // Get icon component for a subject
    const getSubjectIcon = (subject) => {
        const IconComponent = subjectIcons[subject] || Book;
        return <IconComponent className="w-6 h-6 text-[#2148C0]" />;
    };

    // const recentActivity = [
    //     { subject: "Mathematics", score: 85, date: "2 hours ago", type: "practice" },
    //     { subject: "Physics", score: 92, date: "1 day ago", type: "exam" },
    //     { subject: "Chemistry", score: 78, date: "2 days ago", type: "practice" }
    // ];

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 17) return "Good afternoon";
        return "Good evening";
    };

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const token = localStorage.getItem("token");
                const res = await fetch(`${API_BASE_URL}/api/users/stats`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                console.log(data)
                setStudyStats(data.stats);
            } catch (error) {
                console.error("❌ Failed to fetch stats:", error);
            }
        };

        fetchStats();
    }, []);


    const statsArray = [
        {
            label: "Total Study Time",
            value: `${studyStats?.total_hours || 0}`,
            trend: studyStats?.hours_trend || "+0m",
            icon: <Clock className="w-5 h-5" />,
            color: "from-blue-500 to-blue-600",
        },
        {
            label: "Study Streak",
            value: `${studyStats?.streak || 0} day${(studyStats?.streak || 0) === 1 ? "" : "s"}`,
            trend: studyStats?.streak_trend || "+0",
            icon: <FaFire className="w-5 h-5" />,
            color: "from-orange-500 to-orange-600",
        },
        {
            label: "Exams Completed",
            value: examStats?.completed_tests || 0,
            trend: examStats?.tests_trend || "+0",
            icon: <FaClipboardCheck className="w-5 h-5" />,
            color: "from-green-500 to-green-600",
        },
        {
            label: "Avg Score",
            value: `${examStats?.average_score || 0}%`,
            trend: examStats?.score_trend || "+0%",
            icon: <FaTrophy className="w-5 h-5" />,
            color: "from-purple-500 to-purple-600",
        },
    ];





    const handleNavigate = (path, comingSoon, requiresSubscription = false) => {
        if (!path || comingSoon) return; // do nothing
        if (requiresSubscription && !subscribed) {
            // show alert or toast
            toast.error("You need an active subscription to access this feature.");
            return;
        }
        navigate(path);
    };


    // Helper to generate recentActivity from exams (regular + JAMB/WAEC).
    const getRecentActivity = (examData, limit = 4) => {
        // Group answers by test.id (promo test ids come through as `promo-<id>`).
        const testsMap = {};
        (examData || []).forEach(answer => {
            if (!answer || !answer.test) return; // skip orphaned answers
            const testId = answer.test.id;
            if (!testsMap[testId]) {
                const isPromo = answer.source === 'promo' || answer.is_promo === 1;
                testsMap[testId] = {
                    isPromo,
                    examType: answer.examType || null,
                    subject: answer.subject,
                    date: answer.test.createdAt,
                    answers: [],
                };
            }
            testsMap[testId].answers.push(answer);
        });

        // Convert to array and calculate score per test
        const testsArray = Object.values(testsMap).map(test => {
            const total = test.answers.length;
            const correct = test.answers.reduce((acc, a) => acc + Number(a.is_correct || 0), 0);
            const score = total ? Math.round((correct / total) * 100) : 0;
            // JAMB/WAEC exams are labelled by exam name; regular tests by subject.
            const label = test.isPromo
                ? (test.examType ? String(test.examType).toUpperCase() : 'EXAM')
                : test.subject;
            return {
                subject: label,
                rawDate: test.date,
                date: timeAgo(test.date), // format nicely
                score,
                type: test.isPromo ? 'promo' : 'exam', // for color coding in UI
                answers: test.answers, // kept so the row can open its result page
            };
        });

        // Sort by test date descending and take the most recent 'limit' tests
        const recentTests = testsArray
            .sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate))
            .slice(0, limit);

        return recentTests;
    };

    // Open a recent test's result page with its answers (works for regular and
    // promo). The dedicated result page reads { subject, selected_option,
    // question } and returns Back to `returnTo`.
    const openRecentResult = (activity) => {
        const result = (activity.answers || []).map((a) => ({
            subject: a.subject,
            selected_option: a.selected_option,
            examType: a.examType,
            is_correct: a.is_correct,
            question: a.quiz || a.question || {},
        }));
        navigate("/activity-result", {
            state: {
                result,
                returnTo: "/dashboard",
                title: activity.subject ? `${activity.subject} Result` : "Exam Result",
            },
        });
    };


    const handleLogout = async () => {
        try {
            const token = localStorage.getItem("token");
            const sessionStart = localStorage.getItem("sessionStart");
            const sessionEnd = new Date().toISOString(); // capture logout time

            if (token && sessionStart) {
                // Send session start and end to backend to update session duration
                await fetch(`${API_BASE_URL}/api/users/update-hours`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ sessionStart, sessionEnd }),
                });
            }
        } catch (error) {
            console.error("Error updating user stats:", error);
        } finally {
            // Clear localStorage and redirect to login
            localStorage.removeItem("token");
            localStorage.removeItem("sessionStart");
            window.location.href = "/login";
        }
    };


    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            {/* Header */}
            <Toaster position="top-right" richColors />

            <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/20 shadow-sm">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">

                        {/* Left: Logo + Live + Timer */}
                        <div className="flex items-center space-x-6">
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#2148C0] to-blue-600 bg-clip-text text-transparent flex items-center">
                                <FaGraduationCap className="mr-3 text-[#2148C0]" />
                                TeeChaa
                            </h1>

                            <div className="hidden md:flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-full">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-sm text-blue-700 font-medium">Live</span>
                            </div>

                            {/* Countdown Timer here */}
                            <div className="hidden md:flex items-center">
                                <CountdownTimer />
                            </div>
                        </div>

                        {/* Right: Notifications + Profile + Logout */}
                        <div className="flex items-center space-x-3">
                            <div className="relative">
                                <FaBell
                                    onClick={() => navigate('/notification')}
                                    className="text-gray-600 text-lg cursor-pointer hover:text-[#2148C0] transition-colors"
                                />
                                {unreadCount > 0 && (
                                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                        <span className="text-xs text-white font-bold">{unreadCount}</span>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center space-x-3 bg-white rounded-full p-1 shadow-sm border">
                                <button
                                    onClick={() => handleNavigate('/profile')}
                                    className="text-gray-700 hover:text-[#2148C0] px-3 py-2 rounded-full transition-colors duration-200 font-medium"
                                >
                                    <User2 />
                                </button>
                                <button
                                    onClick={handleLogout}
                                    className="text-gray-600 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-all duration-200"
                                >
                                    <FaSignOutAlt />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Mobile: place timer below logo */}
                    <div className="mt-3 md:hidden flex justify-center">
                        <CountdownTimer />
                    </div>
                </div>
            </header>
            {/* <SubscriptionStatus /> */}
            <main className="container mx-auto px-4 py-8 max-w-7xl">
                {/* Welcome Section */}
                <div className="mb-8">
                    <div className="bg-gradient-to-r from-[#2148C0] to-blue-600 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-12 -mb-12"></div>

                        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between">
                            <div>
                                <h2 className="text-2xl md:text-4xl font-bold mb-2">
                                    {getGreeting()}, {userData?.firstName}!
                                    <span className="inline-block ml-2 animate-bounce">👋</span>
                                </h2>
                                <p className="text-blue-100 mb-4 md:mb-0 text-lg">
                                    Ready to achieve greatness today? Let's make it count!
                                </p>
                                {localStorage.getItem("role_id") && (
                                    <div className="inline-flex items-center gap-3 bg-white/20 border border-white/30 rounded-xl px-4 py-2 mt-3 backdrop-blur-sm">
                                        <span className="text-xs uppercase tracking-widest text-blue-100">Your ID</span>
                                        <span className="font-mono font-bold text-white text-lg tracking-wider">
                                            {localStorage.getItem("role_id")}
                                        </span>
                                    </div>
                                )}
                                <div className="flex items-center space-x-4 text-sm text-blue-200 mt-2">
                                    <span className="flex items-center">
                                        <Clock className="w-4 h-4 mr-1" />
                                        {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    <span className="flex items-center">
                                        <Calendar className="w-4 h-4 mr-1" />
                                        {currentTime.toLocaleDateString()}
                                    </span>
                                </div>
                            </div>

                            <div className="flex space-x-4 mt-4 md:mt-0">
                                <button
                                    onClick={() => handleNavigate('/take/exam')}
                                    className="bg-white text-[#2148C0] px-6 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                                >
                                    <Play className="w-4 h-4 inline mr-2" />
                                    Start Exam
                                </button>
                                <button
                                    onClick={() => handleNavigate('/take/study')}
                                    className="border-2 border-white text-white px-6 py-3 rounded-xl font-semibold hover:bg-white hover:text-[#2148C0] transition-all duration-200"
                                >
                                    Practice
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Stats Overview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
                    {statsArray.map((stat, index) => {
                        const isPositive = stat.trend.startsWith("+");
                        return (
                            <div
                                key={index}
                                className="bg-white p-4 md:p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:-translate-y-1 border border-gray-100"
                                onMouseEnter={() => setActiveCard(index)}
                                onMouseLeave={() => setActiveCard(null)}
                            >
                                <div
                                    className={`w-12 h-12 bg-gradient-to-r ${stat.color} rounded-xl flex items-center justify-center text-white mb-3 transition-transform duration-300 ${activeCard === index ? "scale-110" : ""
                                        }`}
                                >
                                    {stat.icon}
                                </div>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                                        <p className="text-sm text-gray-600">{stat.label}</p>
                                    </div>
                                    <span
                                        className={`text-xs px-2 py-1 rounded-full font-semibold ${isPositive
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                            }`}
                                    >
                                        {stat.trend}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Quick Actions */}
                <div className="mb-12">
                    {/* <h3 className="text-2xl font-bold text-gray-800 mb-8 flex items-center">
                        <Zap className="mr-3 w-7 h-7 text-[#2148C0]" />
                        Quick Actions
                    </h3> */}
                    <div className="space-y-8">
                        {/* Main Actions */}
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <Star className="w-5 h-5 text-yellow-500" />
                                <h3 className="font-bold text-gray-800 text-lg">Core Actions</h3>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-6">
                                {quickActions.slice(0, 4).map((action) => (
                                    <div
                                        key={action.id}
                                        onClick={() => handleNavigate(action.path, action.comingSoon, action.requiresSubscription)}
                                        className={`group relative bg-white p-6 rounded-2xl shadow-md transition-all duration-300 border border-gray-100 overflow-hidden
                ${action.comingSoon
                                                ? "opacity-60 cursor-not-allowed"
                                                : "cursor-pointer hover:shadow-lg hover:-translate-y-2"
                                            }`}
                                    >
                                        {/* Hover overlay */}
                                        {!action.comingSoon && (
                                            <div
                                                className={`absolute inset-0 bg-gradient-to-r ${action.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
                                            ></div>
                                        )}

                                        <div className="relative z-10 flex flex-col items-center text-center space-y-3">
                                            <div
                                                className={`text-4xl transition-transform duration-300 ${action.comingSoon
                                                    ? "opacity-70"
                                                    : "group-hover:scale-110"
                                                    }`}
                                            >
                                                {action.icon}
                                            </div>
                                            <h4
                                                className={`font-semibold text-gray-800 transition-colors duration-200 ${action.comingSoon
                                                    ? ""
                                                    : "group-hover:text-[#2148C0]"
                                                    }`}
                                            >
                                                {action.title}
                                            </h4>
                                            <p
                                                className={`text-sm text-gray-600 leading-snug transition-colors duration-200 ${action.comingSoon
                                                    ? ""
                                                    : "group-hover:text-gray-700"
                                                    }`}
                                            >
                                                {action.description}
                                            </p>

                                            {action.comingSoon && (
                                                <span className="text-xs text-[#2148C0] font-medium mt-1">
                                                    🔥Coming soon
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Other Tools */}
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <Zap className="w-5 h-5 text-gray-600" />
                                <h3 className="font-bold text-gray-800 text-lg">Quick Actions </h3>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-6">
                                {quickActions.slice(4).map((action) => (
                                    <div
                                        key={action.id}
                                        onClick={() => handleNavigate(action.path, action.comingSoon, action.requiresSubscription)}
                                        className="group relative bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:-translate-y-2 border border-gray-100 overflow-hidden"
                                    >
                                        {/* Hover overlay */}
                                        <div
                                            className={`absolute inset-0 bg-gradient-to-r ${action.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}
                                        ></div>

                                        <div className="relative z-10 flex flex-col items-center text-center space-y-3">
                                            <div className="text-4xl group-hover:scale-110 transition-transform duration-300">
                                                {action.icon}
                                            </div>
                                            <h4 className="font-semibold text-gray-800 group-hover:text-[#2148C0] transition-colors duration-200">
                                                {action.title}
                                            </h4>
                                            <p className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors duration-200 leading-snug">
                                                {action.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>


                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Subjects Section */}
                    <div className="lg:col-span-2">
                        {userSubjects.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-xl font-bold text-gray-800 flex items-center">
                                        <BookOpen className="mr-3 w-6 h-6 text-[#2148C0]" />
                                        My Subjects
                                        <span className="ml-3 bg-gradient-to-r from-[#2148C0] to-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                                            {userSubjects.length}
                                        </span>
                                    </h3>
                                    <button
                                        onClick={() => handleNavigate('/update-academics')}
                                        className="flex items-center text-sm text-[#2148C0] hover:text-blue-700 font-medium transition-colors duration-200 bg-blue-50 px-3 py-2 rounded-lg hover:bg-blue-100"
                                    >
                                        <Settings className="mr-2 w-4 h-4" />
                                        Manage
                                    </button>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    {[...displayedSubjects] // make a copy so original isn't mutated
                                        .sort((a, b) => a.localeCompare(b)) // alphabetical sort
                                        .map((subject, index) => (
                                            <div
                                                key={index}
                                                className="group bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl border border-gray-100 hover:shadow-lg hover:border-[#2148C0] transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
                                                onClick={() => handleNavigate(`/take-exam?subject=${encodeURIComponent(subject)}`)}
                                            >
                                                <div className="text-center">
                                                    <div className="mb-3 flex justify-center group-hover:scale-110 transition-transform duration-300">
                                                        {getSubjectIcon(subject)}
                                                    </div>
                                                    <p className="text-sm font-semibold text-gray-700 group-hover:text-[#2148C0] transition-colors duration-200 leading-tight">
                                                        {subject}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                </div>


                                {userSubjects.length > 6 && (
                                    <div className="text-center mt-6">
                                        <button
                                            onClick={() => setShowAllSubjects(!showAllSubjects)}
                                            className="inline-flex items-center text-sm text-[#2148C0] hover:text-blue-700 font-medium transition-colors duration-200 bg-blue-50 px-4 py-2 rounded-lg hover:bg-blue-100"
                                        >
                                            {showAllSubjects ? (
                                                <>
                                                    <ChevronUp className="mr-2 w-4 h-4" />
                                                    Show Less
                                                </>
                                            ) : (
                                                <>
                                                    <ChevronDown className="mr-2 w-4 h-4" />
                                                    Show More ({userSubjects.length - 6} more)
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Recent Activity */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                                <BarChart3 className="mr-2 w-5 h-5 text-[#2148C0]" />
                                Recent Activity
                            </h3>
                            <div className="space-y-3">
                                {recentActivity.length > 0 ? (
                                    recentActivity.map((activity, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            onClick={() => openRecentResult(activity)}
                                            className="w-full text-left flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div className={`w-3 h-3 rounded-full ${activity.type === 'promo' ? 'bg-[#2148C0]' : 'bg-red-500'}`}></div>
                                                <div>
                                                    <p className="font-medium text-gray-800 text-sm">{activity.subject}</p>
                                                    <p className="text-xs text-gray-600">{activity.date}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span className={`text-sm font-bold ${activity.score >= 80 ? 'text-green-600' : activity.score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                                                    {activity.score}%
                                                </span>
                                                <FaStar className={`w-3 h-3 ${activity.score >= 80 ? 'text-yellow-500' : 'text-gray-400'}`} />
                                            </div>
                                        </button>
                                    ))
                                ) : (
                                    <p className="text-gray-500 text-sm">No recent activity</p>
                                )}
                            </div>
                        </div>

                        {/* Study Tip */}
                        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl border border-yellow-200 p-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
                                <Brain className="mr-2 w-5 h-5 text-yellow-600" />
                                💡 Study Tip
                            </h3>
                            <p className="text-gray-700 leading-relaxed text-sm">
                                "Break your study sessions into 25-minute focused blocks with 5-minute breaks. This Pomodoro technique helps maintain concentration and prevents burnout!"
                            </p>
                            <div className="mt-4 flex items-center text-xs text-yellow-700">
                                <Coffee className="w-3 h-3 mr-1" />
                                Powered by TeeChaa AI
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="mt-16 bg-gradient-to-r from-gray-900 to-gray-800 text-white py-8">
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <FaGraduationCap className="text-2xl mr-2" />
                            <span className="text-xl font-bold">TeeChaa</span>
                        </div>
                        <p className="text-gray-400 text-sm">
                            &copy; {new Date().getFullYear()} TeeChaa CBT Application. Empowering students to achieve excellence.
                        </p>
                        <div className="mt-4 flex items-center justify-center space-x-6 text-sm text-gray-400">
                            <span>Made with ❤️ for students</span>
                            <span>•</span>
                            <span>AI-Powered Learning</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    )
};

export default Dashboard;